/**
 * Scannen: Kamera (BarcodeDetector auf Android Chrome, sonst zxing-wasm – iPad-Safari hat keinen BarcodeDetector)
 * und Bluetooth-Handscanner als Tastatur-Eingabe (Keyboard-Wedge: Zeichen in schneller Folge, Abschluss mit Enter).
 * Das WASM wird selbst gehostet (keine externen Quellen zur Laufzeit).
 */
import { useEffect, useRef, useState, type RefObject } from "react";
import { prepareZXingModule, readBarcodes } from "zxing-wasm/reader";
import zxingWasmUrl from "zxing-wasm/reader/zxing_reader.wasm?url";

let zxingBereit = false;
function zxingVorbereiten() {
  if (zxingBereit) return;
  zxingBereit = true;
  prepareZXingModule({ overrides: { locateFile: (pfad: string, prefix: string) => (pfad.endsWith(".wasm") ? zxingWasmUrl : prefix + pfad) }, fireImmediately: true });
}

interface BarcodeDetectorLike {
  detect(source: HTMLVideoElement | ImageBitmap | HTMLCanvasElement): Promise<{ rawValue: string }[]>;
}
declare global {
  interface Window {
    BarcodeDetector?: new (opts: { formats: string[] }) => BarcodeDetectorLike;
  }
}

export type KameraStatus = "aus" | "startet" | "aktiv" | "verweigert" | "fehlt";

/** Kamera-Scanner: liest QR-Codes aus dem Videobild, meldet jeden Code höchstens einmal je 1,5 s. */
export function useKameraScanner(videoRef: RefObject<HTMLVideoElement | null>, aktiv: boolean, onCode: (code: string) => void) {
  const [status, setStatus] = useState<KameraStatus>("aus");
  const cb = useRef(onCode);
  cb.current = onCode;

  useEffect(() => {
    if (!aktiv) {
      setStatus("aus");
      return;
    }
    const video = videoRef.current;
    if (!video) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus("fehlt");
      return;
    }
    let stream: MediaStream | null = null;
    let laeuft = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let zuletzt = { code: "", zeit: 0 };
    const detector = window.BarcodeDetector ? new window.BarcodeDetector({ formats: ["qr_code"] }) : null;
    if (!detector) zxingVorbereiten();
    const canvas = document.createElement("canvas");

    const melde = (code: string) => {
      const jetzt = Date.now();
      if (code === zuletzt.code && jetzt - zuletzt.zeit < 1500) return;
      zuletzt = { code, zeit: jetzt };
      cb.current(code);
    };

    const lese = async () => {
      if (!laeuft) return;
      try {
        if (video.readyState >= 2 && video.videoWidth > 0) {
          if (detector) {
            const codes = await detector.detect(video);
            for (const c of codes) if (c.rawValue) melde(c.rawValue);
          } else {
            const w = Math.min(640, video.videoWidth);
            const h = Math.round((video.videoHeight / video.videoWidth) * w);
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext("2d", { willReadFrequently: true });
            if (ctx) {
              ctx.drawImage(video, 0, 0, w, h);
              const bild = ctx.getImageData(0, 0, w, h);
              const ergebnisse = await readBarcodes(bild, { formats: ["QRCode"], tryHarder: false, maxNumberOfSymbols: 1 });
              for (const r of ergebnisse) if (r.text) melde(r.text);
            }
          }
        }
      } catch {
        // einzelnes Bild übersprungen
      }
      if (laeuft) timer = setTimeout(() => void lese(), 120);
    };

    setStatus("startet");
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false })
      .then((s) => {
        if (!laeuft) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        stream = s;
        video.srcObject = s;
        video.setAttribute("playsinline", "true");
        video.muted = true;
        return video.play().then(() => {
          setStatus("aktiv");
          void lese();
        });
      })
      .catch(() => setStatus("verweigert"));

    return () => {
      laeuft = false;
      if (timer) clearTimeout(timer);
      stream?.getTracks().forEach((t) => t.stop());
      if (video) video.srcObject = null;
    };
  }, [aktiv, videoRef]);

  return status;
}

/** Bluetooth-/USB-Handscanner: tippt den Code als Tastatur und schließt mit Enter ab. */
export function useTastaturScanner(aktiv: boolean, onCode: (code: string) => void) {
  const cb = useRef(onCode);
  cb.current = onCode;
  useEffect(() => {
    if (!aktiv) return;
    let puffer = "";
    let letzteZeit = 0;
    const onKey = (e: KeyboardEvent) => {
      const ziel = e.target as HTMLElement | null;
      const inFeld = ziel && (ziel.tagName === "INPUT" || ziel.tagName === "TEXTAREA" || ziel.isContentEditable) && !ziel.hasAttribute("data-scanner");
      if (inFeld) return;
      const jetzt = Date.now();
      if (jetzt - letzteZeit > 150) puffer = "";
      letzteZeit = jetzt;
      if (e.key === "Enter") {
        if (puffer.length >= 8) {
          e.preventDefault();
          cb.current(puffer);
        }
        puffer = "";
      } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        puffer += e.key;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [aktiv]);
}

/** Kurzer Ton beim Scan (WebAudio, kein externes Asset). */
export function scanTon(art: "ok" | "warnung") {
  try {
    const ctx = new AudioContext();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.value = art === "ok" ? 1200 : 400;
    g.gain.value = 0.08;
    o.connect(g).connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + (art === "ok" ? 0.08 : 0.25));
    o.onended = () => void ctx.close();
  } catch {
    // kein Ton verfügbar
  }
}
